import styled, { keyframes } from "styled-components";
import { useEffect, useState } from "react";

const slideIn = keyframes`
  from {
    transform: translate(-50%, -100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translate(-50%, 0);
    opacity: 1;
  }
  to {
    transform: translate(-50%, -100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  animation: ${props => props.isVisible ? slideIn : slideOut} 0.3s ease;
  max-width: 600px;
  width: 90%;
`;

const ToastContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: var(--interactive-primary);
  color: var(--text-inverse);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: var(--font-size-base);
  line-height: 20px;
`;

const ToastIcon = styled.span`
  font-size: 20px;
  flex-shrink: 0;
`;

const ToastMessage = styled.div`
  flex: 1;

  strong {
    font-weight: var(--font-weight-semibold);
  }
`;

const ToastAction = styled.button`
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: var(--text-inverse);
  font-weight: var(--font-weight-semibold);
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

interface Props {
  practice: string;
  patient: string;
  onUndo: () => void;
  duration?: number;
}

const PreferenceToast = ({ practice, patient, onUndo, duration = 5000 }: Props) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleUndo = () => {
    setIsVisible(false);
    onUndo();
  };

  if (!isVisible) return null;

  const patientLabel = patient === "Adult" ? "Adult" : "Child";
  const practiceLabel = practice === "Primary" ? "Community" : 
                        practice === "Secondary" ? "Hospital" : "Prophylaxis";

  return (
    <ToastContainer isVisible={isVisible}>
      <ToastContent>
        <ToastIcon>✓</ToastIcon>
        <ToastMessage>
          Using your saved preferences: <strong>{patientLabel} & {practiceLabel}</strong>
        </ToastMessage>
        <ToastAction onClick={handleUndo}>
          Undo
        </ToastAction>
      </ToastContent>
    </ToastContainer>
  );
};

export default PreferenceToast;